import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Drawer, IconButton, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MONO_FONT, PAGE_FONT } from "../../components/metrics/metricsUi";
import { isMobile } from "../../globalStyles/mobile";
import { purchaseOrderAnalysisService } from "../../utils/services/purchaseOrderAnalysisService";
import { fmtUnits, riskColor } from "../../utils/purchaseOrderAnalysisPageDefaults";
import { SectionCard, useLatestRequest } from "./parts";
import SkuDetail from "./SkuDetail";

const SUGGEST_DEBOUNCE_MS = 250;

/** Debounced SKU search over every SKU ever on a PO, or stocked/sold here. */
export const SkuSearch = ({ ui, base, value, onSelect, width = 360 }) => {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(input.trim()), SUGGEST_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);
  const suggestions = useLatestRequest(
    query.length >= 2 && base.warehouse_id
      ? (signal) => purchaseOrderAnalysisService.fetchSku({ ...base, search: query }, { signal })
      : null,
    [query, base.warehouse_id, base.client_id],
  );
  const options = suggestions.data?.suggestions || [];
  // Autocomplete resets the typed text whenever `value` is a new object, so keep
  // it stable per selected SKU — otherwise every keystroke snaps the text back.
  const selected = useMemo(() => (value ? { sku: value } : null), [value]);

  return (
    <Autocomplete
      size="small"
      sx={{ width }}
      options={options}
      value={selected}
      inputValue={input}
      onInputChange={(_event, next) => setInput(next)}
      onChange={(_event, option) => {
        // The clear (×) button sends null: clear the lookup, not just the text.
        onSelect(option && option.sku ? option.sku : null);
        if (!option) setInput("");
      }}
      getOptionLabel={(option) => option.sku || ""}
      isOptionEqualToValue={(option, selected) => option.sku === selected.sku}
      filterOptions={(list) => list}
      loading={suggestions.loading}
      noOptionsText={query.length < 2 ? "Type at least 2 characters" : "No SKU matches"}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.sku} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start !important" }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%" }}>
            <Box component="span" sx={{ fontFamily: MONO_FONT, fontSize: 12.5, fontWeight: 600 }}>{option.sku}</Box>
            {option.risk ? (
              <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: riskColor(option.risk) }} />
            ) : null}
            <Box component="span" sx={{ ml: "auto", fontSize: 11, color: ui.colors.textTertiary }}>
              {option.on_hand != null ? `${fmtUnits(option.on_hand)} on hand` : ""}
              {option.on_po ? "" : " · never on a PO"}
            </Box>
          </Box>
          {option.product_name ? (
            <Box component="span" sx={{ fontSize: 11.5, color: ui.colors.textSecondary }}>{option.product_name}</Box>
          ) : null}
        </Box>
      )}
      renderInput={(params) => <TextField {...params} placeholder="Look up a SKU…" />}
    />
  );
};

/** The page's SKU lookup section: search, then the SKU's detail inline. */
const SkuLookup = ({ ui, base, sku, onSku, onOpenPo, id }) => (
  <SectionCard
    id={id}
    ui={ui}
    title="SKU purchase history"
    subtitle="Every PO a SKU has been on (all warehouses, any age), with its stock runway at this warehouse."
    right={<SkuSearch ui={ui} base={base} value={sku} onSelect={onSku} width={isMobile() ? "100%" : 360} />}
  >
    {sku ? (
      <SkuDetail ui={ui} sku={sku} base={base} onOpenPo={onOpenPo} />
    ) : (
      <Typography sx={{ fontSize: 12.5, color: ui.colors.textSecondary }}>
        Search for a SKU, or click any SKU in the tables above.
      </Typography>
    )}
  </SectionCard>
);

/** The same SKU detail in a drawer, opened from table rows. */
export const SkuDrawer = ({ ui, sku, base, onClose, onOpenPo }) => {
  const mobile = isMobile();
  return (
    <Drawer
      anchor="right"
      open={Boolean(sku)}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{ sx: { width: mobile ? "100%" : 820, backgroundColor: ui.colors.bgPaper, fontFamily: PAGE_FONT } }}
    >
      <Box sx={{ p: mobile ? 2 : 3, textAlign: "left" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography sx={{ fontSize: 11.5, color: ui.colors.textSecondary }}>SKU purchase history</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {sku ? <SkuDetail ui={ui} sku={sku} base={base} onOpenPo={onOpenPo} /> : null}
      </Box>
    </Drawer>
  );
};

export default SkuLookup;
